import type {
  DisplayContext,
  OverrideAudit,
  PricingEngineOverrides,
} from '@frontend/api';

export type ProfitItemCellData = {
  id: number;
  profitItemId: number;
  quantityId: number;
  profit: string;
  manualProfit: string | null;
  percentage: number | null;
  manualPercentage: number | null;
  overridesAudit: OverrideAudit[] | null;
  unitProfit: string;
};

type BaseProfitItemData = {
  id: number;
  name: string;
  profitItemDefId: number;
  componentId: number;
  displayContext: DisplayContext;
  notes: string | null;
  calculatedNotes: string | null;
  overrides: PricingEngineOverrides | null;
  overridesAudit: OverrideAudit[] | null;
  fieldOverrideAudits: Record<string, OverrideAudit[]> | null;
  position: number;
  calculationType: ProfitItemCalculationType;
};

export const PROFIT_ITEM_CATEGORY = {
  MATERIAL: 'material',
  INSIDE: 'inside',
  OUTSIDE: 'outside',
  PURCHASED_COMPONENT: 'purchased_component',
  CHILD_OVERRIDE: 'child_override',
  GENERAL: 'general',
  CUSTOM: 'custom',
} as const;

export type ProfitItemCategory =
  (typeof PROFIT_ITEM_CATEGORY)[keyof typeof PROFIT_ITEM_CATEGORY];

export const PROFIT_ITEM_CALCULATION_TYPE = {
  MARKUP: 'markup',
  MARGIN: 'margin',
} as const;

export type ProfitItemCalculationType =
  (typeof PROFIT_ITEM_CALCULATION_TYPE)[keyof typeof PROFIT_ITEM_CALCULATION_TYPE];

/**
 * Discriminated union on `category`. When category is 'custom', the custom
 * name/color fields are present. This is a soft convention maintained by the
 * application layer — not guaranteed by a DB constraint.
 */
export type ProfitItemData =
  | (BaseProfitItemData & {
      category: 'custom';
      customCategoryName: string;
      customCategoryColor: string;
    })
  | (BaseProfitItemData & {
      category: Exclude<ProfitItemCategory, 'custom'>;
      customCategoryName: null;
      customCategoryColor: null;
    });

export type ProfitItemWithCells = ProfitItemData & {
  cells: ProfitItemCellData[];
};
